import { useEffect, useMemo } from "react";
import { useMediaQuery } from "react-responsive";
import { useUser } from "@clerk/react";
import { Outlet, useLocation } from "react-router-dom";

import Navbar from "@/components/v2/navbar";
import TopNav from "@/components/v2/top-nav";
import { THEME_STORAGE_KEY as THEME_KEY } from "@/layouts/theme-switch";
import BackgroundBlobs from "@/components/ui/background-blobs";

const BG_CLASS = "h-screen w-screen inset-0 opacity-100 dark:opacity-100 absolute z-2";

function getBgPair(pathname) {
  if (pathname === "/") {
    return { light: "/bg-light-3.png", dark: "/bg-dark-sm.png" };
  }
  if (pathname === "/network" || pathname === "/profile") {
    return { light: "/bg-light-3.png", dark: "/bg-dark-lg.svg" };
  }
  return { light: "/bg-light-3.png", dark: "/bg-dark-sm.png" };
}

export default function DefaultLayout() {
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const { user, isSignedIn, isLoaded } = useUser();
  const { pathname } = useLocation();

  const { withInfinity, infinityVariant } = useMemo(() => {
    if (pathname === "/" || ["/today", "/upcoming", "/past"].includes(pathname)) {
      return { withInfinity: true, infinityVariant: "md" };
    } else if (pathname === "/profile") {
      return { withInfinity: true, infinityVariant: "lg" };
    }
    return { withInfinity: false, infinityVariant: "lg" };
  }, [pathname]);

  const renderBg = () => {
    const { light, dark } = getBgPair(pathname);
    return (
      <>
      {/* <BackgroundBlobs className="z-1 opacity-80"/> */}
        <img src={light} className={`${BG_CLASS} dark:hidden`} />
        <img src={dark} className={`${BG_CLASS} hidden dark:block`} />
      </>
    );
  };

  const renderInfinity = () => {
    if (!withInfinity) return null;
    const sizing =
      infinityVariant === "lg" ? "min-w-[550px] h-[623px] left-1/2 -translate-x-1/2" : "";
    const base = `top-0 left-12 absolute z-10 inset-0  ${sizing}`;
    return (
      <>
        <img src="/infinity-light-2.png" className={`${base} block dark:hidden ${infinityVariant === "lg" ? "opacity-50 left-1/2!" : ""}`} />
        <img
          src="/infinity-dark-3.png"
          className={`${base} hidden dark:block ${infinityVariant === "lg" ? "opacity-30 left-1/2!" : "left-10! opacity-50 "}`}
        />
      </>
    );
  };

  useEffect(() => {
    if (!isLoaded || !isSignedIn || !user) return;

    const saved = (user.unsafeMetadata || {})[THEME_KEY];

    if (saved === "light" || saved === "dark") {
      document.documentElement.classList.toggle("dark", saved === "dark");
      try {
        window.localStorage.setItem(THEME_KEY, saved);
      } catch (err) {
        console.error("Failed to sync theme to localStorage:", err);
      }
    } else {
      const current = document.documentElement.classList.contains("dark") ? "dark" : "light";
      void user
        .updateMetadata({ unsafeMetadata: { [THEME_KEY]: current } })
        .catch((e) => console.error("Failed to seed theme in Clerk:", e));
    }
  }, [isLoaded, isSignedIn, user]);

  if (isMobile) {
    return (
      <div className="relative h-screen max-h-screen overflow-hidden w-full flex flex-col dark:bg-black">
        <TopNav />

        <div
          className="flex flex-col flex-1 relative z-30 overflow-y-auto scroll-smooth"
          style={{ touchAction: "pan-y" }}
        >
          <main className="overflow-auto min-h-0">
            <div className="container mx-auto max-w-7xl flex flex-col min-h-0">
              <div className="section-padding-x flex-1 min-h-0 flex flex-col overflow-auto" id="page-content">
                <Outlet />
              </div>
            </div>
          </main>
        </div>

        {renderInfinity()}

        {renderBg()}

        <div className="bg-white/10 h-screen w-screen z-2 backdrop-blur-[3px] dark:backdrop-blur-[100px] absolute inset-0 top-0 left-0 right-0" />

        <div className="fixed bottom-5.5 left-5.5 right-5.5 z-40 pointer-events-none">
          <div className="pointer-events-auto">
            <Navbar />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full relative flex items-center justify-center bg-neutral-100 dark:bg-neutral-900">
      <div
        id="ui_root"
        className={`relative h-screen w-[390px] flex flex-col overflow-hidden
          rounded-[28px] border border-neutral-200 dark:border-neutral-700
          shadow-2xl ring-1 ring-black/5
          bg-white/90 dark:bg-neutral-900/90 backdrop-blur`}
      >
        <div className="absolute top-0 left-0 right-0 z-50">
          <TopNav />
        </div>

        <div
          className="flex-1 min-h-0 overflow-y-auto scroll-smooth overscroll-contain pt-[70px]"
          style={{
            touchAction: "pan-y",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <Outlet />
        </div>

        {renderBg()}

        <div className="bg-white/10 h-screen w-screen z-10 absolute inset-0 top-0 left-0 right-0" />

        <div className="fixed bottom-5.5 left-5.5 right-5.5 z-20 pointer-events-none">
          <div className="pointer-events-auto">
            <Navbar />
          </div>
        </div>
      </div>
    </div>
  );
}